import { Button, CardMedia, Input, TextField } from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import { useNavigate, useParams } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Category, CommonBody } from "../../types/General";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import {
  STORAGE_KEYS,
  errorToast,
  getFromStorage,
  successToast,
} from "../../helpers";
import {
  useAddCategoryMutation,
  useLazyGetCategoryByIdQuery,
  useUpdateCategoryMutation,
} from "../../services/main";
import { UploadMedia } from "../../utils/mediaUpload";
import { isString } from "../../utils/validation";
import Loader from "../../constants/Loader";
import { useAppDispatch } from "../../hooks/store";
import useAuth from "../../hooks/useAuth";
import { setCredentials } from "../../reducers/authSlice";

export default function AddCategory() {
  const { id } = useParams();
  const user = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [addCategory, { isLoading }] = useAddCategoryMutation();
  const [updateCategory, updateCategoryData] = useUpdateCategoryMutation();
  const [categoryById] = useLazyGetCategoryByIdQuery();

  const [error, setError] = useState<boolean>(false);
  const [category, setCategory] = useState<Category | null>(null);

  //for image upload
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target;
    const file = files?.files?.length ? files?.files[0] : "";

    // Check if a file is selected
    if (file) {
      // Check if the selected file is an image
      if (file.type.startsWith("image/")) {
        const res = await UploadMedia(file);
        if (res?.statusCode === 200) {
          formik.setFieldValue("image", res?.data);
        } else {
          errorToast(res?.message);
        }
      } else {
        // Display an error message for non-image files
        errorToast("Please Try Again");
      }
    }
  };

  const getCategoryById = async (id: string) => {
    const token = getFromStorage(STORAGE_KEYS.token);

    dispatch(
      setCredentials({
        user: user,
        token: token,
      })
    );
    try {
      const response = await categoryById({ id }).unwrap();
      if (response?.statusCode === 200) {
        setCategory(response?.data);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: category?.name || "",
      description: category?.description || "",
      image: category?.image || "",
    },
    validationSchema: Yup.object({
      image: Yup.string().required("Image name is required"),
      name: Yup.string()
        .required("Category name is required")
        .min(2, "Minimum 2 characters are required")
        .max(80, "Maximum 80 character are allowed"),
      description: Yup.string()
        .required("Description is required")
        .min(2, "Minimum 2 characters are required")
        .max(280, "Maximum 280 character are allowed"),
    }),
    onSubmit: async () => {
      formik.setSubmitting(true);
      let body = {
        name: formik.values.name,
        description: formik.values.description,
        image: formik.values.image,
      };

      if (id) {
        try {
          let encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateCategory({
            body: encryptedBody,
            id,
          }).unwrap();
          if (response?.statusCode === 200) {
            successToast("Category Updated Successfully");
            formik.resetForm();
            navigate("/manage-category");
          } else {
            errorToast(response?.message || "");
          }
        } catch (error: any) {
          errorToast(error?.data?.message || "");
        }
      } else {
        try {
          let encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

          const response = await addCategory(encryptedBody).unwrap();
          if (response?.statusCode === 200) {
            successToast("Category Added Successfully");
            formik.resetForm();
            navigate("/manage-category");
          } else {
            errorToast(response?.message || "");
          }
        } catch (error: any) {
          errorToast(error?.data?.message || "");
        }
      }
    },
  });

  useEffect(() => {
    if (id && id !== "add") {
      getCategoryById(id);
    }
  }, []);

  return (
    <MainContainer name="Contact & Support">
      <Loader isLoad={updateCategoryData?.isLoading || isLoading} />
      <div className="manage_blog">
        <div className="sec_head fs_22">
          <h2>{id ? "Edit" : "Add"} Category</h2>
          <div className="btn_group">
            <button
              className="btn btn_sm btn_primary "
              onClick={() => navigate("/manage-category")}
            >
              Back
            </button>
          </div>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <div className="form_group gap_m">
            <div className="prfl_pic form_control form_control_bg  w_100 ">
              <label>Category Image (dimensions 498×540)</label>
              {formik.values.image ? (
                <div className="upload_image">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia
                        component="img"
                        image={formik.values.image}
                        alt="photo"
                      />
                    </figure>
                    <CloseIcon
                      onClick={() => {
                        formik.setFieldValue("image", "");
                      }}
                    />
                  </div>
                </div>
              ) : (
                <label className="upload_image" htmlFor="icon-button-file">
                  <Input
                    sx={{ display: "none" }}
                    id="icon-button-file"
                    type="file"
                    inputProps={{
                      accept: "image/png,image/jpeg",
                    }}
                    onChange={handleImageUpload}
                  />
                  <span className="upload_image_holder">
                    <figure>
                      <img src="/images/placeholder_gallery.jpeg" alt="" />
                    </figure>
                    <AddIcon />
                  </span>

                  {!formik.values.image && error ? (
                    <h6 className="err_msg">This field is required</h6>
                  ) : (
                    ""
                  )}
                </label>
              )}
            </div>

            <div className="form_control form_control_bg  w_50">
              <label> Category Name </label>
              <TextField
                hiddenLabel
                placeholder="Category Name"
                fullWidth
                type="text"
                variant="outlined"
                className="text_field"
                name="name"
                id="name"
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                helperText={formik.touched.name && formik.errors.name}
              />
            </div>
            <div className="form_control form_control_bg w_50">
              <label>Category Description</label>

              <TextField
                multiline
                maxRows={4}
                minRows={4}
                hiddenLabel
                placeholder="Description goes here"
                fullWidth
                name="description"
                type="text"
                variant="outlined"
                className="text_field"
                id="description"
                onChange={(val) => {
                  if (val.target.value === " " || val.target.value === ".") {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                value={formik.values.description}
                helperText={
                  formik.touched.description && formik.errors.description
                }
              />
            </div>

            <div className="submit_btn">
              <Button
                className="btn btn_primary"
                color="primary"
                size="large"
                type="submit"
                variant="contained"
                onClick={() => setError(true)}
              >
                Submit
              </Button>
            </div>
          </div>
        </form>
      </div>
    </MainContainer>
  );
}
